import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

// OpenAI endpoints are on the v1 Node.js server (/api/), not Laravel (/api/v2/)
const V1_BASE = '/api'
function v1Headers(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = localStorage.getItem('auth_token')
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

async function v1Get<T>(path: string): Promise<T> {
  const res = await fetch(`${V1_BASE}${path}`, { headers: v1Headers() })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

async function v1Post<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${V1_BASE}${path}`, {
    method: 'POST',
    headers: v1Headers(),
    body: body ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

async function v1Delete<T>(path: string): Promise<T> {
  const res = await fetch(`${V1_BASE}${path}`, { method: 'DELETE', headers: v1Headers() })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

interface OpenAIStatus {
  // "connected" = o provedor de IA escolhido pelo usuario esta pronto (ChatGPT conectado
  // ou Gemini com chave). As telas com a varinha de IA leem so este campo.
  connected: boolean
  connectedAt: string | null
  // Conexao do ChatGPT em si (compartilhada), independente do provedor escolhido
  chatgptConnected?: boolean
  provider?: 'chatgpt' | 'gemini'
}

interface DeviceCodeResponse {
  device_auth_id: string
  user_code: string
  verification_uri: string
  interval: number
  expires_at: string
}

interface DevicePollResponse {
  status: 'pending' | 'authorized'
  accountId?: string
}

export function useOpenAIStatus() {
  return useQuery<OpenAIStatus>({
    queryKey: ['openai', 'status'],
    queryFn: () => v1Get('/openai/status'),
    staleTime: 30 * 1000,
  })
}

// Device code request — via Codex device auth endpoint
export function useOpenAIDeviceCode() {
  return useMutation<DeviceCodeResponse>({
    mutationFn: () => v1Post('/openai/device-code'),
  })
}

// Device code poll — polls until user authorizes on OpenAI
export function useOpenAIDevicePoll(
  deviceAuthId: string | null,
  userCode: string | null,
  enabled: boolean,
) {
  return useQuery<DevicePollResponse>({
    queryKey: ['openai', 'device-poll', deviceAuthId],
    queryFn: () =>
      v1Post('/openai/device-poll', {
        device_auth_id: deviceAuthId,
        user_code: userCode,
      }),
    enabled: !!deviceAuthId && !!userCode && enabled,
    refetchInterval: (query) => {
      const data = query.state.data
      if (data?.status === 'authorized') return false
      return 5000
    },
    refetchIntervalInBackground: true,
  })
}

export function useOpenAIDisconnect() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => v1Delete('/openai/disconnect'),
    onSuccess: () => {
      queryClient.setQueryData(['openai', 'status'], { connected: false, connectedAt: null })
    },
  })
}

// Analyze clip frames with GPT
interface AnalyzeClipParams {
  frameUrls: string[]
  aiLog?: import('@/types').AiLog
  clipFilename?: string
}

interface AnalyzeClipResponse {
  analysis: string
  raw?: unknown
}

export function useAnalyzeClip() {
  return useMutation<AnalyzeClipResponse, Error, AnalyzeClipParams>({
    mutationFn: (params) => v1Post('/openai/analyze-clip', params),
  })
}

// Generate caption based on recent IG posts
interface GenerateCaptionParams {
  igUserId: string
  videoTitle?: string
}

interface GenerateCaptionResponse {
  caption: string
}

export function useGenerateCaption() {
  return useMutation<GenerateCaptionResponse, Error, GenerateCaptionParams>({
    mutationFn: (params) => v1Post('/openai/generate-caption', params),
  })
}

// Generate YouTube title + description based on channel videos
interface GenerateYtMetaParams {
  videos: Array<{ title: string; description?: string | null; tags?: string[] }>
  savedTags?: string[]
  count?: number
}

interface GenerateYtMetaItem {
  title: string
  description: string
  tags: string
}

interface GenerateYtMetaResponse {
  title?: string
  description?: string
  tags?: string
  items?: GenerateYtMetaItem[]
}

export function useGenerateYtMeta() {
  return useMutation<GenerateYtMetaResponse, Error, GenerateYtMetaParams>({
    mutationFn: (params) => v1Post('/openai/generate-yt-meta', params),
  })
}

// AI Agent — streaming chat
export interface AgentMessage {
  role: 'user' | 'assistant'
  content: string
}

export async function streamAgentMessage(
  messages: AgentMessage[],
  onChunk: (text: string) => void,
  signal?: AbortSignal,
): Promise<string> {
  const res = await fetch(`${V1_BASE}/openai/agent`, {
    method: 'POST',
    headers: v1Headers(),
    body: JSON.stringify({ messages }),
    signal,
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }

  if (!res.body) throw new Error('No response body')

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let fullText = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    const lines = buffer.split('\n')
    buffer = lines.pop() || ''

    let eventType = ''
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7).trim()
      } else if (line.startsWith('data: ') && eventType) {
        try {
          const data = JSON.parse(line.slice(6))
          if (eventType === 'delta' && data.text) {
            fullText += data.text
            onChunk(fullText)
          }
          if (eventType === 'error') {
            throw new Error(data.error || 'Erro do agente')
          }
        } catch (e) {
          if (e instanceof Error && e.message !== 'Unexpected end of JSON input') throw e
        }
        eventType = ''
      }
    }
  }

  return fullText
}
